/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging/Log";
import type { IPlayer } from "../interfaces";
import {
  EmulatedTrickplayControlImpl,
  IFrameTrickplayControlImpl,
  ThumbnailTrickplayControlImpl
} from "./impls";
import { Direction, SpeedLevel, TrickPlayMode } from "../enums";
import type {
  ITrickPlayControl,
  ITrickPlayConfig,
  ITrickPlayEligibility
} from "../interfaces";

/**
 * Central orchestrator for trick-play operations.
 *
 * Owned by and accessed through `VideoManager` — consumer code
 * (Redux thunks, controllers) reaches this via
 * `VideoManager.getTrickPlayManager()`.
 *
 * Selects the appropriate ITrickPlayControl implementation based on
 * runtime capability detection (Thumbnail > IFrame > Emulated) and
 * routes all abstract input intents to the active implementation.
 *
 * Enforces:
 *   - Trick-play restriction API gating (PRD 7.1 / TP-AP-1..4)
 *   - Platform x channel eligibility (PRD 5)
 *   - Boundary auto-stop with directional ignore (TP-LP-9, TP-FF-8, TP-SK-9)
 *   - Counter-direction speed reduction (TP-FF-9)
 *   - Mode-specific confirm behavior (TP-SP-8)
 *
 * Does NOT know about physical inputs, platforms, or UI.
 */
export class TrickPlayManager {
  private static readonly TAG: string = "TrickPlayManager";

  /** Reference to the active player instance. */
  private mPlayer: IPlayer;

  /** Trick-play configuration values. */
  private mConfig: ITrickPlayConfig;

  /** Platform x channel eligibility rules (PRD 5). */
  private mEligibility: ITrickPlayEligibility;

  /** Currently active trick-play implementation, null before init(). */
  private mImpl: ITrickPlayControl | null = null;

  /** Current speed level during an active hold/cycle session (null for skip). */
  private mCurrentLevel: SpeedLevel | null = null;

  /** Current seek direction during an active hold/cycle session. */
  private mCurrentDirection: Direction | null = null;

  /** Playback position when the seek session began. */
  private mSeekEntryPoint: number | null = null;

  /** Whether a trick-play session is currently in progress. */
  private mIsActive: boolean = false;

  /** True after the restriction API returns an allowed response (TP-AP-1). */
  private mRestrictionEnabled: boolean = false;

  /**
   * Direction blocked by boundary auto-stop (TP-LP-9, TP-FF-8, TP-SK-9).
   * Further seek inputs in this direction are ignored until reset.
   */
  private mBoundaryBlockedDirection: Direction | null = null;

  /**
   * Backward seek limit (epoch seconds) that overrides the seekable range
   * start, letting the seek motion continue past the live buffer start into
   * the Start Over stream instead of stopping there. Null uses the seekable
   * range start as before (no extension).
   */
  private mStartOverLowerBoundEpoch: number | null = null;

  constructor(
    player: IPlayer,
    config: ITrickPlayConfig,
    eligibility: ITrickPlayEligibility
  ) {
    this.mPlayer = player;
    this.mConfig = config;
    this.mEligibility = eligibility;
  }

  // ── Private Methods ────────────────────────────────────────────────────────

  /**
   * Select the trick-play implementation based on capability.
   * Precedence: Thumbnail > IFrame > Emulated.
   *
   * @returns The selected ITrickPlayControl implementation.
   */
  private selectImpl(): ITrickPlayControl {
    if (this.mConfig.enableThumbnailSeek) {
      return new ThumbnailTrickplayControlImpl(this.mPlayer, this.mConfig, () =>
        this.getEffectiveLowerBound()
      );
    }
    if (this.mConfig.hasTrickPlayTrack) {
      return new IFrameTrickplayControlImpl(this.mPlayer, this.mConfig);
    }
    return new EmulatedTrickplayControlImpl(this.mPlayer, this.mConfig);
  }

  /**
   * Record the seek entry point if this is the first action in a session.
   */
  private markEntryIfIdle(): void {
    if (this.mSeekEntryPoint === null) {
      this.mSeekEntryPoint = this.mPlayer.getCurrentTime();
    }
  }

  /**
   * Guard: check eligibility (PRD 5) and restriction API (PRD 7.1).
   *
   * @returns True if the operation is allowed, false if blocked.
   */
  private guardEligibility(): boolean {
    return this.mRestrictionEnabled && this.mEligibility.trickPlayAllowed;
  }

  /**
   * Guard against forward seek when fast-forward is restricted, and
   * against backward seek when rewind is restricted. Driven by the
   * `allowForward` / `allowBackward` flags on eligibility, which are
   * derived from the session-level `trickModeRestrictions` payload.
   *
   * @param direction - The requested seek direction.
   * @returns True if the operation is allowed, false if blocked.
   */
  private guardDirection(direction: Direction): boolean {
    if (direction === Direction.Forward) {
      return this.mEligibility.allowForward;
    }
    return this.mEligibility.allowBackward;
  }

  /**
   * Check whether a direction is blocked by boundary auto-stop.
   * TP-SK-9, TP-LP-9, TP-FF-8: further same-direction inputs are ignored.
   * Opposite-direction inputs are still accepted.
   *
   * @param direction - The requested seek direction.
   * @returns True if the direction is currently blocked.
   */
  private isBoundaryBlocked(direction: Direction): boolean {
    if (
      this.mBoundaryBlockedDirection !== null &&
      this.mBoundaryBlockedDirection !== direction
    ) {
      this.mBoundaryBlockedDirection = null;
    }
    return this.mBoundaryBlockedDirection === direction;
  }

  /**
   * After a discrete skip, check whether the impl target has reached a
   * seekable range boundary. If so, mark the direction as blocked
   * so further same-direction skips are ignored (TP-SK-9).
   *
   * @param direction - The direction of the skip that just occurred.
   */
  private checkBoundaryAfterSkip(direction: Direction): void {
    if (this.mImpl === null) {
      return;
    }
    const range = this.mPlayer.getSeekableRange();
    const pos = this.mImpl.getTargetPosition();
    const threshold = this.mConfig.boundaryThreshold;
    const lowerBound = this.getEffectiveLowerBound();
    if (direction === Direction.Backward && pos <= lowerBound + threshold) {
      this.mBoundaryBlockedDirection = Direction.Backward;
    }
    if (direction === Direction.Forward && pos >= range.end - threshold) {
      this.mBoundaryBlockedDirection = Direction.Forward;
    }
  }

  /**
   * Reset internal state after a session ends (cancel / confirm).
   */
  private reset(): void {
    this.mIsActive = false;
    this.mSeekEntryPoint = null;
    this.mCurrentLevel = null;
    this.mCurrentDirection = null;
    this.mBoundaryBlockedDirection = null;
  }

  // ── Public Methods — Lifecycle ─────────────────────────────────────────────

  /**
   * Initialize the manager by selecting the appropriate trick-play impl.
   * Call after playback is ready and capability information is available.
   */
  public init(): void {
    this.mImpl = this.selectImpl();
    Log.d(TrickPlayManager.TAG, "init() selected mode=" + this.mImpl.mode);
  }

  /**
   * Update the restriction API state.
   * Called by VideoManager after the restriction API resolves or
   * on recovery after a previous failure (TP-AP-3).
   *
   * @param enabled - True if trick-play is allowed, false if restricted.
   */
  public setRestrictionEnabled(enabled: boolean): void {
    this.mRestrictionEnabled = enabled;
    Log.d(TrickPlayManager.TAG, "setRestrictionEnabled() enabled=" + enabled);
  }

  /**
   * Returns the active trick-play configuration.
   */
  public getConfig(): ITrickPlayConfig {
    return this.mConfig;
  }

  /**
   * Set (or clear) the backward seek limit used in place of the seekable
   * range start when a dedicated Start Over stream can be reached by
   * seeking further back than the live buffer start. Called by the owner
   * (VideoPlaybackController) whenever this eligibility changes.
   *
   * @param epoch - Programme start (epoch seconds) to extend seeking to,
   *   or null to fall back to the seekable range start.
   */
  public setStartOverLowerBound(epoch: number | null): void {
    this.mStartOverLowerBoundEpoch = epoch;
  }

  /**
   * Resolve the effective backward seek limit: the Start Over lower bound
   * when one is set and it is earlier than the seekable range start,
   * otherwise the seekable range start itself.
   *
   * @returns Effective backward seek limit in seconds.
   */
  public getEffectiveLowerBound(): number {
    const range = this.mPlayer.getSeekableRange();
    if (
      this.mStartOverLowerBoundEpoch !== null &&
      this.mStartOverLowerBoundEpoch < range.start
    ) {
      return this.mStartOverLowerBoundEpoch;
    }
    return range.start;
  }

  /**
   * Whether trick-play is currently enabled (restriction API + eligibility).
   *
   * @returns True if trick-play controls should be interactive.
   */
  public isEnabled(): boolean {
    return this.mRestrictionEnabled && this.mEligibility.trickPlayAllowed;
  }

  /**
   * Update eligibility rules (e.g. on channel change).
   *
   * @param eligibility - New eligibility state.
   */
  public setEligibility(eligibility: ITrickPlayEligibility): void {
    this.mEligibility = eligibility;
  }

  /**
   * Get the current eligibility rules.
   *
   * @returns The active eligibility state.
   */
  public getEligibility(): ITrickPlayEligibility {
    return this.mEligibility;
  }

  /**
   * Whether the restriction API has granted trick-play (TP-AP-1).
   * Distinct from {@link isEnabled} which also factors in eligibility.
   *
   * @returns True if the restriction API has authorised trick-play.
   */
  public isRestrictionEnabled(): boolean {
    return this.mRestrictionEnabled;
  }

  /**
   * Get the currently active trick-play mode.
   *
   * @returns The active mode, or null if init() has not been called.
   */
  public getMode(): TrickPlayMode | null {
    if (this.mImpl === null) {
      return null;
    }
    return this.mImpl.mode;
  }

  /**
   * Whether a trick-play session is currently active.
   *
   * @returns True if a seek session is in progress.
   */
  public isActive(): boolean {
    return this.mIsActive;
  }

  /**
   * Get the seek entry point (position when trick-play session began).
   *
   * @returns Entry point in seconds, or null if no session is active.
   */
  public getSeekEntryPoint(): number | null {
    return this.mSeekEntryPoint;
  }

  /**
   * Get the current speed level during an active hold/cycle session.
   *
   * @returns The active speed level.
   */
  public getCurrentLevel(): SpeedLevel | null {
    return this.mCurrentLevel;
  }

  /**
   * Get the current seek direction during an active hold/cycle session.
   *
   * @returns The active direction, or null if idle.
   */
  public getCurrentDirection(): Direction | null {
    return this.mCurrentDirection;
  }

  /**
   * Get the impl's current target position.
   * For Thumbnail mode this returns the virtual seek target (player stays
   * paused at entry). For Emulated/IFrame it returns the player's current
   * time (player is already at target).
   *
   * @returns Target position in seconds, or the player's current time
   *          if no impl is active.
   */
  public getTargetPosition(): number {
    if (this.mImpl !== null) {
      return this.mImpl.getTargetPosition();
    }
    return this.mPlayer.getCurrentTime();
  }

  // ── Public Methods — Intent Handlers ───────────────────────────────────────

  /**
   * Handle discrete skip intent (SKIP_FORWARD / SKIP_BACKWARD).
   * Workflow A.
   *
   * @param direction - Forward (+1) or Backward (-1).
   * @returns True if the skip was performed, false if blocked by a guard.
   */
  public skip(direction: Direction): boolean {
    if (!this.guardEligibility()) {
      return false;
    }
    if (!this.guardDirection(direction)) {
      return false;
    }
    if (this.isBoundaryBlocked(direction)) {
      return false;
    }
    if (this.mImpl === null) {
      Log.w(TrickPlayManager.TAG, "skip() - impl not initialized");
      return false;
    }
    // Short-jump while a cycle seek is running: pause continuous
    // motion first, then apply the discrete skip from the current position.
    if (this.mCurrentLevel !== null) {
      this.mImpl.pauseScrubbing();
      this.mCurrentLevel = null;
    }
    // Skip does NOT mark entry point — entry mark is only for
    // hold (long-press) and speed-cycle (FF/RW) sessions.
    this.mCurrentDirection = direction;
    this.mIsActive = true;
    this.mImpl.skip(direction);
    this.checkBoundaryAfterSkip(direction);
    return true;
  }

  /**
   * Handle hold intent (HOLD_FORWARD / HOLD_BACKWARD).
   * Workflow B — continuous variable-speed seek.
   *
   * @param direction - Forward (+1) or Backward (-1).
   * @param level - Speed escalation level.
   */
  public hold(direction: Direction, level: SpeedLevel): void {
    if (!this.guardEligibility()) {
      return;
    }
    if (!this.guardDirection(direction)) {
      return;
    }
    if (this.isBoundaryBlocked(direction)) {
      return;
    }
    if (this.mImpl === null) {
      Log.w(TrickPlayManager.TAG, "hold() - impl not initialized");
      return;
    }
    this.markEntryIfIdle();
    this.mCurrentLevel = level;
    this.mCurrentDirection = direction;
    this.mIsActive = true;
    this.mImpl.fastForward(direction, level);
  }

  /**
   * Handle speed-cycle intent (SPEED_CYCLE_FORWARD / SPEED_CYCLE_BACKWARD).
   * Workflow C — discrete speed step per press with counter-direction
   * speed reduction (TP-FF-9).
   *
   * @param direction - Forward (+1) or Backward (-1).
   */
  public speedCycle(direction: Direction): void {
    if (!this.guardEligibility()) {
      return;
    }
    if (!this.guardDirection(direction)) {
      return;
    }
    if (this.isBoundaryBlocked(direction)) {
      return;
    }
    if (this.mImpl === null) {
      Log.w(TrickPlayManager.TAG, "speedCycle() - impl not initialized");
      return;
    }

    // Counter-direction speed reduction (TP-FF-9):
    // If currently active in opposite direction, reduce speed by one level.
    // At L1 in opposite direction -> exit trick-play, resume normal playback.
    if (
      this.mIsActive &&
      this.mCurrentDirection !== null &&
      this.mCurrentDirection !== direction
    ) {
      if (this.mCurrentLevel !== null && this.mCurrentLevel > SpeedLevel.L1) {
        this.mCurrentLevel = (this.mCurrentLevel - 1) as SpeedLevel;
        this.mImpl.fastForward(this.mCurrentDirection, this.mCurrentLevel);
        Log.d(
          TrickPlayManager.TAG,
          "speedCycle() counter-direction reduce to level=" + this.mCurrentLevel
        );
        return;
      }
      // At L1 in opposite direction -> exit trick-play
      Log.d(
        TrickPlayManager.TAG,
        "speedCycle() counter-direction at L1, exiting trick-play"
      );
      this.confirm();
      return;
    }

    this.markEntryIfIdle();
    if (!this.mIsActive) {
      this.mCurrentLevel = SpeedLevel.L1;
      this.mCurrentDirection = direction;
      this.mIsActive = true;
    } else {
      this.mCurrentLevel = Math.min(
        (this.mCurrentLevel || 0) + 1,
        SpeedLevel.L3
      ) as SpeedLevel;
    }
    this.mImpl.fastForward(direction, this.mCurrentLevel);
    Log.d(
      TrickPlayManager.TAG,
      "speedCycle() direction=" + direction + " level=" + this.mCurrentLevel
    );
  }

  /**
   * Handle scrub begin intent (SCRUB_BEGIN).
   * Workflow D.
   *
   * @param positionSec - Starting scrub position in seconds.
   */
  public scrubBegin(positionSec: number): void {
    if (!this.guardEligibility()) {
      return;
    }
    if (this.mImpl === null) {
      Log.w(TrickPlayManager.TAG, "scrubBegin() - impl not initialized");
      return;
    }
    this.markEntryIfIdle();
    this.mIsActive = true;
    this.mImpl.onScrubBegin(positionSec);
  }

  /**
   * Handle scrub move intent (SCRUB_MOVE).
   * Workflow D.
   *
   * @param positionSec - Current scrub target position in seconds.
   */
  public scrubMove(positionSec: number): void {
    if (!this.guardEligibility()) {
      return;
    }
    if (!this.mIsActive || this.mImpl === null) {
      return;
    }
    this.mImpl.onScrubMove(positionSec);
  }

  /**
   * Handle scrub end intent (SCRUB_END).
   * Workflow D.
   *
   * @param positionSec - Final committed position in seconds.
   */
  public scrubEnd(positionSec: number): void {
    if (!this.guardEligibility()) {
      return;
    }
    if (!this.mIsActive || this.mImpl === null) {
      return;
    }
    this.mImpl.onScrubEnd(positionSec);
    this.mImpl.stopScrubbing();
    this.reset();
  }

  /**
   * Handle cancel intent (CANCEL).
   * Workflow E — stop active session and return to entry point.
   */
  public cancel(): void {
    if (!this.mIsActive) {
      return;
    }
    if (this.mImpl !== null) {
      this.mImpl.stopScrubbing();
    }
    if (this.mSeekEntryPoint !== null) {
      this.mPlayer.seek(this.mSeekEntryPoint);
    }
    this.mPlayer.play();
    Log.d(
      TrickPlayManager.TAG,
      "cancel() returning to entry=" + this.mSeekEntryPoint
    );
    this.reset();
  }

  /**
   * Handle confirm intent (CONFIRM).
   * Workflow F — stop active session and resume playback.
   *
   * Mode-specific behavior (TP-SP-8):
   *   - Thumbnail: player was paused at seek entry point;
   *     reads target position from impl, seeks to target, then plays.
   *   - Emulated / Fullscreen i-Frame: player is already at target
   *     position; no additional seek needed, just plays.
   */
  public confirm(): void {
    if (!this.mIsActive) {
      this.mPlayer.play();
      return;
    }
    if (this.mImpl !== null) {
      // TP-SP-8: mode-specific confirm behavior
      if (this.mImpl.mode === TrickPlayMode.Thumbnail) {
        // Thumbnail: player at entry point -> seek to target
        const target = this.mImpl.getTargetPosition();
        this.mImpl.stopScrubbing();
        this.mPlayer.seek(target);
      } else {
        // Emulated / IFrame: player already at target position
        this.mImpl.stopScrubbing();
      }
    }
    this.mPlayer.play();
    Log.d(TrickPlayManager.TAG, "confirm() resuming playback");
    this.reset();
  }

  /**
   * Stop any active trick-play without seeking back to entry point.
   * Used for hold-release (stop seeking, stay at position, remain paused).
   */
  public stop(): void {
    if (this.mImpl !== null) {
      this.mImpl.stopScrubbing();
    }
    Log.d(TrickPlayManager.TAG, "stop()");
    this.reset();
  }

  /**
   * Pause the active seek motion but keep the session alive.
   * Called on hold key-release so the user can confirm (OK) or
   * cancel (BACK) before the session ends. The speed indicator
   * is hidden (`mCurrentLevel` set to null) while the session
   * remains active (`mIsActive` preserved).
   *
   * For Thumbnail mode, the tick timer driving virtual-position
   * updates is cleared; the player stays paused at the entry point.
   * For Emulated mode, the tick timer is cleared; the player
   * stays at whatever position it last seeked to.
   */
  public holdRelease(): void {
    if (!this.mIsActive) {
      return;
    }
    if (this.mImpl !== null) {
      this.mImpl.pauseScrubbing();
    }
    this.mCurrentLevel = null;
    Log.d(
      TrickPlayManager.TAG,
      "holdRelease() — seek paused, speed indicator hidden, awaiting OK/BACK"
    );
  }

  /**
   * Called by the boundary watcher (TIME_UPDATE listener) when the player
   * reaches a seekable range boundary during active trick-play.
   * Implements TP-LP-9, TP-FF-8 boundary auto-stop.
   *
   * For Thumbnail mode, seeks to the target position (boundary)
   * before ending the session so the player is not left at the entry point.
   * Emulated/IFrame already have the player at the boundary position.
   *
   * @param direction - The direction that hit the boundary.
   */
  public onBoundaryReached(direction: Direction): void {
    if (!this.mIsActive) {
      return;
    }
    if (this.mImpl !== null) {
      // For Thumbnail: commit seek to boundary position
      if (this.mImpl.mode === TrickPlayMode.Thumbnail) {
        const target = this.mImpl.getTargetPosition();
        this.mPlayer.seek(target);
      }
      this.mImpl.stopScrubbing();
    }
    Log.d(
      TrickPlayManager.TAG,
      "onBoundaryReached() direction=" + direction + " blocked"
    );
    this.reset();
    this.mBoundaryBlockedDirection = direction;
  }

  // ── Public Methods — Navigation ────────────────────────────────────────────

  /**
   * Jump to the live edge (seek range end).
   * Stops any active trick-play session and seeks to live position.
   * Only allowed when `goLiveAvailable` is true (PRD §5).
   */
  public goToLive(): void {
    if (this.mIsActive) {
      if (this.mImpl !== null) {
        this.mImpl.stopScrubbing();
      }
      this.reset();
    }
    const range = this.mPlayer.getSeekableRange();
    this.mPlayer.seek(range.end);
    this.mPlayer.play();
    Log.d(TrickPlayManager.TAG, "goToLive() seeked to " + range.end);
  }

  /**
   * Jump to the start of the seekable buffer (seek range start).
   * Stops any active trick-play session and seeks to buffer start.
   * Only allowed when `startOverAvailable` is true (PRD §5).
   */
  public startFromBuffer(): void {
    if (this.mIsActive) {
      if (this.mImpl !== null) {
        this.mImpl.stopScrubbing();
      }
      this.reset();
    }
    const range = this.mPlayer.getSeekableRange();
    this.mPlayer.seek(range.start);
    Log.d(TrickPlayManager.TAG, "startFromBuffer() seeked to " + range.start);
  }
}
